import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { IDestination } from 'src/app/masters/places/destination/destination.interface';
import { IDestinationCriteria, IPermissionWithShow } from 'src/app/shared/interface';
import { IVesselVoyage, IVesselVoyageCriteria } from '../../vessel-voyage/vessel-voyage-interface';
import { DatePipe } from '@angular/common';
import { PermissionService } from 'src/app/shared/services/permissions.service';
import { MatDialog } from '@angular/material/dialog';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { DataService } from 'src/app/shared/services/data.service';
import { PermissionKey } from 'src/app/shared/constants/permissionCode';
import { debounceTime, distinctUntilChanged, exhaustMap, map, take, takeUntil, tap } from 'rxjs/operators';
import { Api } from 'src/app/shared/constants/api';
import { APP_ROUTES } from 'src/app/shared/constants/approutes';
import { CartingPoints } from 'src/app/shared/constants/carting-points';
import { Message } from 'src/app/shared/constants/message';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ShareWeeklyConsolEmailComponent } from '../share-weekly-consol-email/share-weekly-consol-email.component';
import * as _ from 'underscore';
import html2canvas from 'html2canvas';

@Component({
    selector: 'app-share-weekly-consol-form',
    templateUrl: './share-weekly-consol-form.component.html',
    styleUrls: ['./share-weekly-consol-form.component.less'],
    standalone: false
})
export class ShareWeeklyConsolFormComponent implements OnInit {

  // The carting point shown in the planning template footer (see CartingPoints to change it or add another one).
  readonly cartingAddressLines = CartingPoints.JWR_CFS_ADDRESS.split('\n');
  readonly cartingContactLines = CartingPoints.JWR_CFS_CONTACTS.split('\n');

  shareWeeklyConsolForm: FormGroup;
  preparedBody: any;
  hoveredDate: Date | null = null;
  polSearchCtrl = this.formBuilder.control('');
  filteredPol: ReplaySubject<IDestination[]> = new ReplaySubject<IDestination[]>(1);
  destinationSearchCtrl = this.formBuilder.control('');
  filteredDestinations: ReplaySubject<IDestination[]> = new ReplaySubject<IDestination[]>(1);
  destinationDD: any = [];
  destinationCriteria: IDestinationCriteria = {};
  onDestroy = new Subject<void>();
  searching: boolean = false;
  weeklyDetailsDataSource = new BehaviorSubject<AbstractControl[]>([]);
  readonly allColumnOrder = ['select', 'copyRow', 'destination', 'vesselVoyage', 'hazPlanning', 'transitTime',
    'tentativeStuffingDate', 'portCutoff', 'hazCutoff', 'siCutoff', 'etdPol', 'routingId'];

  columnOptions = [
    { key: 'destination', label: 'Destination' },
    { key: 'vesselVoyage', label: 'Vessel / Voyage' },
    { key: 'hazPlanning', label: 'Planning Type' },
    { key: 'transitTime', label: 'Approx Transit Time' },
    { key: 'tentativeStuffingDate', label: 'Tentative Stuffing' },
    { key: 'portCutoff', label: 'Port Cutoff' },
    { key: 'hazCutoff', label: 'Haz Cutoff' },
    { key: 'siCutoff', label: 'SI Cutoff' },
    { key: 'etdPol', label: 'ETD POL' },
    { key: 'routingId', label: 'Routing' },
  ];

  visibleColumnKeys = new Set<string>([
    'destination', 'vesselVoyage', 'hazPlanning', 'transitTime', 'tentativeStuffingDate', 'etdPol', 'routingId'
  ]);

  columnFilterOpen = false;

  weeklyColumns = this.allColumnOrder.filter(col =>
    col === 'select' || col === 'copyRow' || this.visibleColumnKeys.has(col)
  );
  vesselVoyages: any = [];
  vesselVoyageSearchCtrl = this.formBuilder.control('');
  filteredVesselVoyages: ReplaySubject<IVesselVoyage[]> = new ReplaySubject<IVesselVoyage[]>(1);
  subscription = new Subscription();
  destinationFilterCtrl = this.formBuilder.control('');
  destinationIdFilterCtrl = this.formBuilder.control(null);
  vesselVoyageFilterCtrl = this.formBuilder.control('');
  stuffingDateFilterCtrl = this.formBuilder.control('');
  routingIdFilterCtrl = this.formBuilder.control('');
  sendWeeklyData: any;
  editPermission: boolean = false;
  viewPermission: boolean = false;
  sendPermission: boolean = false;
  noDataAvailable: boolean = false;
  selectAll: boolean = false;
  destinationSortDirection: 'asc' | 'desc' | '' = '';
  etdPolSortDirection: 'asc' | 'desc' | '' = '';
  tentativeStuffingDateSortDirection: 'asc' | 'desc' | '' = '';
  
  planningImageData: any[] = [];
  isGeneratingImage: boolean = false;
  @ViewChild('planningTemplate')
  planningTemplate!: ElementRef;

  constructor(private formBuilder: FormBuilder, private utilityService: UtilityService, private router: Router,
    private service: DataService, private logger: NGXLogger, private activatedRoute: ActivatedRoute,
    private dialog: MatDialog, private permissionService: PermissionService, private datePipe: DatePipe,) { }

  ngOnInit(): void {
    this.initializeForm();
    this.getAllPermission();
    this.getDestination();
    this.filterPol();
    this.filterDestination();
    this.setupColumnFilters();
    this.resetDestinationFilterOnChange();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  setupColumnFilters() {
    this.destinationFilterCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(value => this.applyColumnFilters());

    this.vesselVoyageFilterCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.applyColumnFilters());

    this.stuffingDateFilterCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.applyColumnFilters());

    this.routingIdFilterCtrl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.applyColumnFilters());

    this.destinationIdFilterCtrl.valueChanges
      .subscribe(() => this.applyColumnFilters());

  }

  private resetDestinationFilterOnChange() {
    this.shareWeeklyConsolForm.get('fromDate')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.destinationIdFilterCtrl.setValue(null, { emitEvent: false }));

    this.shareWeeklyConsolForm.get('polId')?.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.destinationIdFilterCtrl.setValue(null, { emitEvent: false }));
  }

  applyColumnFilters() {
    const allRows = this.planningDetails.controls;
    const destVal = (this.destinationFilterCtrl.value || '').toLowerCase();
    const vvVal = (this.vesselVoyageFilterCtrl.value || '').toLowerCase();
    const stuffingVal = (this.stuffingDateFilterCtrl.value || '').toLowerCase();
    const routingVal = (this.routingIdFilterCtrl.value || '').toLowerCase();

    const selectedDestId = this.destinationIdFilterCtrl.value;

    const filtered = allRows.filter(ctrl => {
      const rowDestId = ctrl.get('destinationId')?.value;
      if (selectedDestId != null && rowDestId !== selectedDestId) return false;

      const destName = this.destinationDD?.find(d => d.id === rowDestId)?.name?.toLowerCase() || '';
      const vvName = this.vesselVoyages?.find(vv => vv.id === ctrl.get('vesselVoyageId')?.value)?.vesselName?.toLowerCase() || '';
      const stuffingDate = (ctrl.get('tentativeStuffingDate')?.value || '').toString().toLowerCase();
      const routingIdValue = ctrl.get('routingId')?.value;
      const routingIdOptions = ctrl.get('routingIdOptions')?.value || [];
      const routingIdOption = routingIdOptions.find(opt => opt.id === routingIdValue);
      const routingId = (routingIdOption?.name || routingIdValue || '').toString().toLowerCase();

      return (
        destName.includes(destVal) &&
        vvName.includes(vvVal) &&
        stuffingDate.includes(stuffingVal) &&
        routingId.includes(routingVal)
      );
    });

    if (this.destinationSortDirection) {
      filtered.sort((a, b) => {
        const nameA = this.destinationDD?.find((d: any) => d.id === a.get('destinationId')?.value)?.name || '';
        const nameB = this.destinationDD?.find((d: any) => d.id === b.get('destinationId')?.value)?.name || '';
        return this.destinationSortDirection === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      });
    }

    if (this.etdPolSortDirection) {
      filtered.sort((a, b) => {
        const dateA = a.get('etdPol')?.value ? new Date(a.get('etdPol').value).getTime() : 0;
        const dateB = b.get('etdPol')?.value ? new Date(b.get('etdPol').value).getTime() : 0;
        return this.etdPolSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }

    if (this.tentativeStuffingDateSortDirection) {
      filtered.sort((a, b) => {
        const dateA = a.get('tentativeStuffingDate')?.value ? new Date(a.get('tentativeStuffingDate').value).getTime() : 0;
        const dateB = b.get('tentativeStuffingDate')?.value ? new Date(b.get('tentativeStuffingDate').value).getTime() : 0;
        return this.tentativeStuffingDateSortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }

    this.weeklyDetailsDataSource.next(filtered);
  }

  sortByDestination(): void {
    this.etdPolSortDirection = '';
    this.tentativeStuffingDateSortDirection = '';
    if (this.destinationSortDirection === '') this.destinationSortDirection = 'asc';
    else if (this.destinationSortDirection === 'asc') this.destinationSortDirection = 'desc';
    else this.destinationSortDirection = '';
    this.applyColumnFilters();
  }

  sortByEtdPol(): void {
    this.destinationSortDirection = '';
    this.tentativeStuffingDateSortDirection = '';
    if (this.etdPolSortDirection === '') this.etdPolSortDirection = 'asc';
    else if (this.etdPolSortDirection === 'asc') this.etdPolSortDirection = 'desc';
    else this.etdPolSortDirection = '';
    this.applyColumnFilters();
  }

  sortByTentativeStuffingDate(): void {
    this.destinationSortDirection = '';
    this.etdPolSortDirection = '';
    if (this.tentativeStuffingDateSortDirection === '') this.tentativeStuffingDateSortDirection = 'asc';
    else if (this.tentativeStuffingDateSortDirection === 'asc') this.tentativeStuffingDateSortDirection = 'desc';
    else this.tentativeStuffingDateSortDirection = '';
    this.applyColumnFilters();
  }

  private initializeForm() {
    this.shareWeeklyConsolForm = this.formBuilder.group({
      id: [],
      weekNo: [null, [Validators.required]],
      yearNo: [null, [Validators.required]],
      fromDate: [null, [Validators.required]],
      toDate: [null, [Validators.required]],
      polId: [null, [Validators.required]],
      planningDetails: this.formBuilder.array([])
    });
    // this.planningDetails.push(this.planningDetailsForm(0));
    this.weeklyDetailsDataSource.next(this.planningDetails.controls);
  }


  private planningDetailsForm(index: number, data?: any): FormGroup {
    return this.formBuilder.group({
      destinationId: [data?.destinationId || null],
      vesselVoyageId: [data?.vesselVoyageId || null],
      isHaz: [data?.isHaz ?? false],
      transitTime: [data?.transitTime || null],
      tentativeStuffingDate: [data?.tentativeStuffingDate || null],
      etdPol: [data?.etdPol || null],
      portCutoff: [data?.portCutoff || null],
      hazCutoff: [data?.hazCutoff || null],
      siCutoff: [data?.siCutoff || null],
      routingId: [data?.routingId != null ? Number(data.routingId) : 0],
      routingIdOptions: [[]],
      select: [false],
      sequenceNo: [index + 1],
      status: [data?.status || null]
    });
  }

  onDateSelected(date: Date | null) {
    if (!date) { return; }

    const monday = this.getWeekStart(date);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const weekNo = this.getISOWeekNumber(monday);
    const yearNo = monday.getFullYear();

    this.shareWeeklyConsolForm.patchValue({
      weekNo,
      yearNo,
      // fromDate: monday,
      toDate: sunday
    });

    const polId = this.shareWeeklyConsolForm.get('polId')?.value;
    if (polId !== null && polId !== undefined && polId !== '') {
      this.checkExistingPlanning(polId);
    }
  }

  private getWeekStart(d: Date): Date {
    const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const day = date.getDay();
    const diff = (day === 0) ? -6 : (1 - day);
    date.setDate(date.getDate() + diff);
    return date;
  }

  private getISOWeekNumber(d: Date): number {
    const date = new Date(d.getTime());
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
    const week1 = new Date(date.getFullYear(), 0, 4);
    const weekNo = 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
    return weekNo;
  }

  highlightWeek = (date: Date) => {
    const refDate =
      this.hoveredDate ||
      this.shareWeeklyConsolForm.get('fromDate')?.value ||
      new Date();

    if (!refDate) return '';

    const monday = this.getWeekStart(refDate);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return (date >= monday && date <= sunday) ? 'week-highlight' : '';
  }

  getDestination() {
    this.destinationCriteria.ids = [this.polId?.value]
      .filter(id => id != null)
    this.service.get(Api.DESTINATION_NAMES_FOR_DD, this.destinationCriteria).subscribe(res => {
      this.logger.info("Destinations: ", res)
      if (res) {
        this.destinationDD = res.contents;
        this.filteredPol.next(this.destinationDD);
        this.filteredDestinations.next(this.destinationDD);
        this.setDefaultPol();
      }
    })
  }

  private setDefaultPol() {
    const existing = this.destinationDD.find(d =>
      d.name?.toLowerCase().includes('nhava sheva')
    );
    if (existing) {
      this.shareWeeklyConsolForm.get('polId')?.setValue(existing.id);
      this.setPolValueChangeEvent(existing.id);
      return;
    }
    this.service.get(Api.DESTINATION_NAMES_FOR_DD, { name: 'Nhava Sheva' }).subscribe(res => {
      const nhavaSheva = res?.contents?.[0];
      if (!nhavaSheva) return;
      if (!this.destinationDD.some(d => d.id === nhavaSheva.id)) {
        this.destinationDD.push(nhavaSheva);
        this.filteredPol.next([...this.destinationDD]);
      }
      this.shareWeeklyConsolForm.get('polId')?.setValue(nhavaSheva.id);
      this.setPolValueChangeEvent(nhavaSheva.id);
    });
  }

  filterPol() {
    this.polSearchCtrl.valueChanges.pipe(tap(() => this.searching = true),
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      exhaustMap(value => {
        if (!value) {
          this.filteredPol.next([...this.destinationDD]);
          return of([...this.destinationDD]);
        }
        return this.service.get(Api.DESTINATION_NAMES_FOR_DD, { name: value }).pipe(
          map(res => res.contents || []),
          tap(filteredResults => {
            this.filteredPol.next([...filteredResults]);
          })
        );
      })
    ).subscribe(() => this.searching = false);
  }

  filterDestination() {
    this.destinationSearchCtrl.valueChanges.pipe(
      tap(() => this.searching = true),
      takeUntil(this.onDestroy),
      debounceTime(200),
      distinctUntilChanged(),
      exhaustMap(value => {
        if (!value) {
          this.filteredDestinations.next([...this.destinationDD]);
          return of([...this.destinationDD]);
        }
        return this.service.get(Api.DESTINATION_NAMES_FOR_DD, { name: value }).pipe(
          map(res => res.contents || []),
          tap(filteredResults => {
            // Ensure selected destinations are retained
            const selectedIds = this.planningDetails.controls
              .map(c => c.get('destinationId')?.value)
              .filter(id => id != null);

            const merged = [
              ...filteredResults,
              ...this.destinationDD.filter(d => selectedIds.includes(d.id) && !filteredResults.some(f => f.id === d.id))
            ];

            this.filteredDestinations.next(merged);
            this.logger.info('Filtered Destinations', this.filterDestination);
          })
        );
      })
    ).subscribe(() => this.searching = false);
  }


  setPolValueChangeEvent(value: number) {
    this.logger.info("polid value: ", value)
    let selectedPol: any;
    this.filteredPol.pipe(take(1)).subscribe(polList => {
      selectedPol = this.destinationDD.find(pol => pol.id === value) ||
        polList.find(pol => pol.id === value);
    });
    if (selectedPol && !this.destinationDD.some(pol => pol.id === selectedPol.id)) {
      this.destinationDD.push(selectedPol);
    }
    this.logger.info("planning form", this.shareWeeklyConsolForm.value)
    this.getVesselVoyagesForETA(value);
  }

  setDestinationValueChangeEvent(value: number, index: number) {
    this.logger.info("Set Destination called", value, index,);
    let selectedDestination: any;
    this.filteredDestinations.pipe(take(1)).subscribe(list => {
      selectedDestination = this.destinationDD.find(dest => dest?.id === value) ||
        list.find(dest => dest?.id === value);
      if (selectedDestination && !this.destinationDD.some(dest => dest?.id === selectedDestination.id)) {
        this.destinationDD.push(selectedDestination);
      }
      if (!list.some(dest => dest.id === value)) {
        this.filteredDestinations.next([...(list || []), selectedDestination]);
      }
    });
    const row = this.planningDetails.at(index);
    if (row) {
      row.get('destinationId')?.setValue(value, { emitEvent: true });
    }
  }

   private fetchAllDestinations(ids: number[], onComplete?: () => void) {
    if (!ids || ids.length === 0) { onComplete?.(); return; }
    const criteria = { ids: ids };
    const pageableParams = {
      page: 0,
      size: ids.length
    };

    this.service.get(Api.DESTINATION_NAMES_FOR_DD,{ ...criteria, ...pageableParams }).subscribe(res => {
      if (!res?.contents?.length) {
        this.logger.error("No destinations found for IDs:", ids);
        return;
      }

      const apiDestinations = res.contents;
      apiDestinations.forEach(dest => {
        if (!this.destinationDD.some(d => d.id === dest.id)) {
          this.destinationDD.push(dest);
        }
      });

      this.filteredDestinations.pipe(take(1)).subscribe(list => {
        const listMap = new Map(list.map(x => [x.id, x]));
        apiDestinations.forEach(dest => listMap.set(dest.id, dest));
        this.filteredDestinations.next(Array.from(listMap.values()));
      });
      this.logger.info("Fetched all destinations:", apiDestinations);
      if (this.destinationSortDirection) {
        this.applyColumnFilters();
      }
      onComplete?.();
    });
  }

  private sortPlanningDetails() {
    const sorted = [...this.planningDetails.controls].sort((a, b) => {
      const nameA = this.destinationDD?.find((d: any) => d.id === a.get('destinationId')?.value)?.name || '';
      const nameB = this.destinationDD?.find((d: any) => d.id === b.get('destinationId')?.value)?.name || '';
      const nameCmp = nameA.localeCompare(nameB);
      if (nameCmp !== 0) return nameCmp;
      const dateA = new Date(a.get('tentativeStuffingDate')?.value || 0).getTime();
      const dateB = new Date(b.get('tentativeStuffingDate')?.value || 0).getTime();
      return dateA - dateB;
    });
    this.planningDetails.clear();
    sorted.forEach(ctrl => this.planningDetails.push(ctrl));
  }

  getVesselVoyagesForETA(polId: number) {

    const now = new Date();
    const cutOffDate = new Date();
    cutOffDate.setHours(15, 0, 0, 0); // 3 PM

    const etaStartDate = new Date();

    if (now <= cutOffDate) {
      etaStartDate.setDate(etaStartDate.getDate() + 2);
    }
    else {
      etaStartDate.setDate(etaStartDate.getDate() + 3);
    }

    const formattedEtaStartDate = this.datePipe.transform(etaStartDate, 'yyyy-MM-dd');
    const formattedNowDate = this.datePipe.transform(now, 'yyyy-MM-dd');

    const criteria: IVesselVoyageCriteria = {
      portId: this.polId.value,
      etdRanges: [formattedNowDate, formattedEtaStartDate]
    };

    this.logger.info('Vessel Voyage Criteria', criteria);

    this.service.get(Api.VESSEL_VOYAGE_FOR_DD, criteria).subscribe(res => {
      if (res) {
        this.vesselVoyages = res;
        this.logger.info("VESSEL VOYAGE DATA", res);
        this.filteredVesselVoyages.next(this.vesselVoyages);
        this.checkExistingPlanning(polId);
      }
    });
  }

  setVesselVoyageValueChangeEvent(vesselVoyageId: number, rowIndex?: number): number[] {
    if (!this.vesselVoyages) return [];

    const selectedVoyage = this.vesselVoyages.find(vv => vv.id === vesselVoyageId);
    if (!selectedVoyage) return [];

    const polId = this.polId.value;
    const portDetail = selectedVoyage.vesselVoyagePortDetails.find(p => p.portId === polId);

    if (!portDetail) return [];

    const rowForm = this.planningDetails.at(rowIndex) as FormGroup;
    if (!rowForm) return [];

    rowForm.patchValue({
      vesselVoyageId: vesselVoyageId,
      tentativeStuffingDate: portDetail.stuffing,
      etdPol: portDetail.etd,
      portCutoff: portDetail.port,
      hazCutoff: portDetail.haz,
      siCutoff: portDetail.si
    });

    const portIds = selectedVoyage.vesselVoyagePortDetails.map(p => p.portId).filter(id => id);

    return portIds;
  }

  private batchLoadRoutingIdOptions(rowPortIdsMap: Map<number, number[]>): void {
    if (!rowPortIdsMap.size) return;

    const allPortIds = [...new Set(([] as number[]).concat(...Array.from(rowPortIdsMap.values())))];
    if (!allPortIds.length) return;

    const criteria: IDestinationCriteria = { ids: allPortIds };
    this.service.get(Api.DESTINATION_NAMES_FOR_DD, criteria).subscribe(res => {
      if (res && res.contents) {
        const destMap = new Map<number, any>(res.contents.map((p: any) => [p.id, p]));

        rowPortIdsMap.forEach((portIds, rowIndex) => {
          const routingIdOptions = [
            { id: 0, name: 'DIRECT', code: 'N/A' },
            { id: -1, name: 'TBA', code: 'N/A' },
            ...portIds.filter(id => destMap.has(id)).map(id => ({ ...destMap.get(id), id }))
          ];

          const rowForm = this.planningDetails.at(rowIndex) as FormGroup;
          if (rowForm) {
            rowForm.get('routingIdOptions')?.setValue(routingIdOptions);
            const savedRoutingId = Number(rowForm.get('routingId')?.value);
            if (routingIdOptions.some(opt => opt.id === savedRoutingId)) {
              rowForm.patchValue({ routingId: savedRoutingId }, { emitEvent: false });
            } else {
              rowForm.patchValue({ routingId: 0 }, { emitEvent: false });
            }
          }
        });
      }
    });
  }

  loadVesselVoyages(voyageIds: number[]): Promise<void> {
    return new Promise((resolve) => {
      if (!voyageIds || voyageIds.length === 0) {
        resolve();
        return;
      }

      const missingIds = voyageIds.filter(id =>
        !this.vesselVoyages?.some(v => v.id === id)
      );

      if (missingIds.length === 0) {
        // nothing to fetch
        resolve();
        return;
      }

      const criteria = { ids: [...new Set(missingIds)] };

      this.service.get(Api.VESSEL_VOYAGES_BY_IDS, criteria).subscribe({
        next: (voyageDetails: any[]) => {
          if (Array.isArray(voyageDetails)) {
            this.vesselVoyages = [...(this.vesselVoyages || []), ...voyageDetails];
            this.filteredVesselVoyages.next(this.vesselVoyages);
          }
          this.logger.info("Vessel Voyage Details",this.vesselVoyages);
          this.logger.info("Vessel Voyage Details",this.filteredVesselVoyages);
          resolve();
        },
        error: err => {
          console.error('Error fetching missing voyages:', err);
          resolve();
        }
      });
    });
  }


  checkExistingPlanning(polId: number) {
    const weekNo = this.shareWeeklyConsolForm.get('weekNo')?.value;
    const yearNo = this.shareWeeklyConsolForm.get('yearNo')?.value;
    const fromDate = this.formatToISODate(this.shareWeeklyConsolForm.get('fromDate')?.value);
    const isDeleted = false;
    const fetchNext = true;

    if (!weekNo || !yearNo || !polId) return;

    const criteria = { weekNo, yearNo, fromDate, polId, isDeleted, fetchNext};

    this.logger.info("Criteria to fetch the Record", criteria);
    this.service.get(Api.WEEKLY_CONSOL_PLANNING + '/getShareConsolPlanningDetails', criteria).subscribe({
      next: async (res: any[]) => {
        this.selectAll = false;
        if (res && res.length > 0) {
          this.logger.info('Existing Weekly planning present for', res);

          // Merge planningDetails from all weeks into one flat list, tagging each with its week for dedup
          const allPlanningDetails = res.reduce((acc: any[], week: any) =>
            acc.concat((week.planningDetails || []).map((d: any) => ({ ...d, weekNo: week.weekNo, yearNo: week.yearNo }))), []);

          // When the same planning (same destination/vessel-voyage/dates/routing) exists for more than one week,
          // keep only the row from the latest week instead of showing duplicate rows
          const mergedPlanningDetails = this.dedupeLatestWeekPlanning(allPlanningDetails);

          this.logger.info("Merged Week Data",mergedPlanningDetails);
          this.planningDetails.clear();

          const voyageIds = [...new Set<number>(
            mergedPlanningDetails
              .map((d: any) => d.vesselVoyageId)
              .filter((id: any) => id != null)
          )];

          // Load all unique voyages before patching rows
          await this.loadVesselVoyages(voyageIds);

          // Build all rows — setVesselVoyageValueChangeEvent patches tentativeStuffingDate from live voyage data
          mergedPlanningDetails.forEach((detail: any, idx: number) => {
            this.planningDetails.push(this.planningDetailsForm(idx, detail));
            if (detail.vesselVoyageId) {
              this.setVesselVoyageValueChangeEvent(detail.vesselVoyageId, idx);
            }
            this.updateCheckboxState(idx);
          });

          // Filter: keep only rows where tentativeStuffingDate >= fromDate (back-to-front to keep indices stable)
          const selectedFromDate = new Date(this.shareWeeklyConsolForm.get('fromDate')?.value);
          selectedFromDate.setHours(0, 0, 0, 0);
          for (let i = this.planningDetails.length - 1; i >= 0; i--) {
            const stuffingVal = this.planningDetails.at(i).get('tentativeStuffingDate')?.value;
            if (!stuffingVal) { this.planningDetails.removeAt(i); continue; }
            const stuffingDate = new Date(stuffingVal);
            stuffingDate.setHours(0, 0, 0, 0);
            if (stuffingDate < selectedFromDate) { this.planningDetails.removeAt(i); }
          }

          // Rebuild rowPortIdsMap with post-filter indices for routingId options
          const rowPortIdsMap = new Map<number, number[]>();
          this.planningDetails.controls.forEach((ctrl, newIdx) => {
            const vesselVoyageId = ctrl.get('vesselVoyageId')?.value;
            if (!vesselVoyageId) return;
            const voyage = this.vesselVoyages?.find((vv: any) => vv.id === vesselVoyageId);
            if (!voyage) return;
            const portIds = (voyage.vesselVoyagePortDetails || []).map((p: any) => p.portId).filter((id: any) => id);
            if (portIds.length) rowPortIdsMap.set(newIdx, portIds);
          });

          this.batchLoadRoutingIdOptions(rowPortIdsMap);

          const remainingControls = this.planningDetails.controls;
          this.noDataAvailable = remainingControls.length === 0;
          this.logger.info('Merged plan loaded for criteria:', criteria, res);

          if (remainingControls.length) {
            const uniqueDestinationIds = [...new Set<number>(
              remainingControls.map(c => c.get('destinationId')?.value).filter((id: any) => id != null)
            )];
            this.fetchAllDestinations(uniqueDestinationIds, () => {
              this.sortPlanningDetails();
              this.weeklyDetailsDataSource.next([...this.planningDetails.controls]);
            });
          } else {
            this.weeklyDetailsDataSource.next([...this.planningDetails.controls]);
          }
        } else {
          this.planningDetails.clear();
          this.weeklyDetailsDataSource.next([...this.planningDetails.controls]);
          this.noDataAvailable = true;
          this.logger.info('No plan exists, starting fresh:', criteria);
        }
      }
    });
  }

  private dedupeLatestWeekPlanning(details: any[]): any[] {
    const keyOf = (d: any) => [
      d.destinationId, d.vesselVoyageId, d.isHaz, d.tentativeStuffingDate, d.etdPol, d.viaPod
    ].join('|');

    const latestByKey = new Map<string, any>();
    details.forEach(detail => {
      const key = keyOf(detail);
      const existing = latestByKey.get(key);
      if (!existing || detail.yearNo > existing.yearNo || (detail.yearNo === existing.yearNo && detail.weekNo > existing.weekNo)) {
        latestByKey.set(key, detail);
      }
    });
    return Array.from(latestByKey.values());
  }


  resetForm() {
    this.shareWeeklyConsolForm.reset();
    this.planningDetails.clear();
  }

  private buildPlanningDetailsPayload() {
    return this.planningDetails.controls.filter(row => row.value.select === true).map((row, index) => {
      const v = row.value;

      const rowForm = row as FormGroup;
      const routingIdOptions = rowForm.get('routingIdOptions')?.value || [];
      // The backend expects the numeric id in routingId (0 = Direct, -1 = TBA) and the label in routingName,
      // which is what the planning email shows.
      let routingIdValue: number | null = null;
      let routingNameValue: string | null = null;

      if (v.routingId != null) {
        const selectedOption = routingIdOptions.find(
          (opt: any) => opt.id === v.routingId
        );
        routingIdValue = Number(v.routingId);
        routingNameValue = selectedOption?.name || null;
      }

      return {
        sequenceNo: index + 1,
        transitTime: v.transitTime,
        destinationId: v.destinationId,
        destinationName: this.destinationDD?.find(x => x.id == v.destinationId)?.name,
        vesselVoyageId: v.vesselVoyageId,
        vesselVoyage: this.vesselVoyages?.find(vv => vv.id == v.vesselVoyageId) ?
          `${this.vesselVoyages.find(vv => vv.id == v.vesselVoyageId).vesselName} (${this.vesselVoyages.find(vv => vv.id == v.vesselVoyageId).voyageNo})` : null,
        isHaz: v.isHaz ? "HAZ" : "Non-HAZ",
        tentativeStuffingDate: this.datePipe.transform(v.tentativeStuffingDate, 'dd-MM-yyyy') || 'NA',
        etdPol: this.datePipe.transform(v.etdPol, 'dd-MM-yyyy') || 'NA',
        portCutoff: this.datePipe.transform(v?.portCutoff, 'dd-MM-yyyy') || 'NA',
        hazCutoff: this.datePipe.transform(v?.hazCutoff, 'dd-MM-yyyy') || 'NA',
        siCutoff: this.datePipe.transform(v?.siCutoff, 'dd-MM-yyyy') || 'NA',
        routingId: routingIdValue,
        routingName: routingNameValue,
        // inHandCbm: v?.inHandCbm,
        // inHandWeight: v?.inHandWeight,
        // shortFallCbm: v?.shortFallCbm,
        // status: v.status
      };
    });
  }

  private buildPlanningContextPayload() {
    const fromDate = this.shareWeeklyConsolForm.get('fromDate')?.value;
    const toDate = this.shareWeeklyConsolForm.get('toDate')?.value;
    return {
      
      weekNo: this.shareWeeklyConsolForm.get('weekNo')?.value,
      yearNo: this.shareWeeklyConsolForm.get('yearNo')?.value,
      fromDate: this.formatToISODate(fromDate),
      toDate: this.formatToISODate(toDate),
      polId: this.shareWeeklyConsolForm.get('polId')?.value,
      polName: this.destinationDD.find(d => d.id == this.shareWeeklyConsolForm.get('polId')?.value).name,
    };
  }

  private formatToISODate(date: any): string | null {
    if (!date) return null;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  toggleSelectAll(event: any) {
  const checked = event.checked;
  this.selectAll = checked;

  const FilteredRows = this.weeklyDetailsDataSource.value;

  FilteredRows.forEach(group => {
    const selectCtrl = group.get('select');
    if (selectCtrl && !selectCtrl.disabled) {
      selectCtrl.setValue(checked);
    }
  });
}

  updateCheckboxState(index: number) {
  const group = this.planningDetails.at(index);
  const status = group.get('status')?.value;
  const selectCtrl = group.get('select');

  if (status === 'CANCELLED' || status === 'PLANNING_COMPLETED') {
    selectCtrl?.disable({ emitEvent: false });
  } else {
    selectCtrl?.enable({ emitEvent: false });
  }
}

  getSelectedCount(): number {
    return this.weeklyDetailsDataSource.value.filter((group: FormGroup) => {
      return group.get('select')?.value === true;
    }).length;
  }

  isEmailDialogEnabled(): boolean {
    const fromDate = this.shareWeeklyConsolForm.get('fromDate')?.value;
    const polId = this.shareWeeklyConsolForm.get('polId')?.value;

    return !!fromDate && !!polId;
  }

  private hasSelectedRows(): boolean {
    return this.planningDetails.controls.some(
      row => row.get('select')?.value === true
    );
  }

  openEmailDialog(): void {
    if (!this.sendPermission) {
      this.utilityService.notifier(Message.SEND_PERMISSION_WARNING, 'warning');
      return;
    }
    if (!this.isEmailDialogEnabled()) {
      this.utilityService.toastrInfoMessage("Please select From Date & POL to proceed");
      return;
    }
    if (!this.hasSelectedRows()) {
      this.utilityService.toastrInfoMessage('Please select at least one planning to send the Email');
      return;
    }

    const dialogRef = this.dialog.open(ShareWeeklyConsolEmailComponent, {
      width: '600px',
      maxWidth: '95vw',
      panelClass: 'mail-dialog-panel',
      data: {
        planningContext: this.buildPlanningContextPayload(),
        planningDetails: this.buildPlanningDetailsPayload()
      },
    });

    dialogRef.afterClosed().subscribe();
  }

  downloadWeeklyPlanning(): void {
    if (!this.isEmailDialogEnabled()) {
      this.utilityService.toastrInfoMessage("Please Select Week & POL to proceed");
      return;
    }
    //Get currently visible rows (after filters)
    const rows = this.weeklyDetailsDataSource.getValue();

    if (!rows || rows.length === 0) {
      this.utilityService.toastrInfoMessage('No data available to export');
      return;
    }

    //convert formGroups to plain object
    const excelData = rows.map((row, index) => {

      const destination =
        this.destinationDD?.find(d => d.id === row.get('destinationId')?.value);

      const vesselVoyage =
        this.vesselVoyages?.find(v => v.id === row.get('vesselVoyageId')?.value);

      return {
        'S.No': index + 1,
        'Destination': destination?.name || 'NA',
        'Vessel / Voyage': vesselVoyage ? `${vesselVoyage.vesselName} (${vesselVoyage.voyageNo})` : 'NA',
        'Transit Time': row.get('transitTime')?.value || 'NA',
        'Tentative Stuffing Date': this.datePipe.transform(row.get('tentativeStuffingDate')?.value, 'dd-MM-yyyy') || 'NA',
        'Port Cutoff': this.datePipe.transform(row.get('portCutoff')?.value, 'dd-MM-yyyy') || 'NA',
        'HAZ Cutoff': this.datePipe.transform(row.get('hazCutoff')?.value, 'dd-MM-yyyy') || 'NA',
        'SI Cutoff': this.datePipe.transform(row.get('siCutoff')?.value, 'dd-MM-yyyy') || 'NA',
        'ETD POL': this.datePipe.transform(row.get('etdPol')?.value, 'dd-MM-yyyy') || 'NA',
        'Routing': this.getRoutingIdName(row),
      };
    });

    //Create worksheet
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelData);

    //Create workbook
    const workbook: XLSX.WorkBook = {
      Sheets: { 'Weekly Planning': worksheet },
      SheetNames: ['Weekly Planning']
    };

    //Export Excel file
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array'
    });

    this.saveExcelFile(excelBuffer, 'Weekly_Consol_Planning-W:'+ this.shareWeeklyConsolForm.get('weekNo').value);
  }

  private saveExcelFile(buffer: any, fileName: string): void {
    const blob = new Blob(
      [buffer],
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
    );

    saveAs(blob, `${fileName}_${new Date().getTime()}.xlsx`);
  }

  getRowIndex(element: AbstractControl): number {
    return this.planningDetails.controls.indexOf(element);
  }

  getDestinationName(element: AbstractControl): string {
    const id = element.get('destinationId')?.value;
    const dest = this.destinationDD?.find((d: any) => d.id === id);
    return dest ? `${dest.name} (${dest.code})` : '- - -';
  }

  getVesselVoyageName(element: AbstractControl): string {
    const id = element.get('vesselVoyageId')?.value;
    const vv = this.vesselVoyages?.find((v: any) => v.id === id);
    return vv ? `${vv.vesselName} (${vv.voyageNo})` : '- - -';
  }

  getRoutingIdName(element: AbstractControl): string {
    const id = Number(element.get('routingId')?.value);
    const options: any[] = element.get('routingIdOptions')?.value || [];
    const pod = options.find(p => p.id === id);
    if (!pod) return '- - -';
    return (id === 0 || id === -1) ? pod.name : `${pod.name} (${pod.code})`;
  }

  copyRowToClipboard(element: AbstractControl): void {
    const destinationId = element.get('destinationId')?.value;
    const vesselVoyageId = element.get('vesselVoyageId')?.value;
    const routingIdId = element.get('routingId')?.value;
    const routingIdOptions: any[] = element.get('routingIdOptions')?.value || [];
    const transitTime = element.get('transitTime')?.value;
    const stuffingDate = element.get('tentativeStuffingDate')?.value;
    const etdPol = element.get('etdPol')?.value;

    const destination = this.destinationDD?.find((d: any) => d.id === destinationId);
    const vesselVoyage = this.vesselVoyages?.find((vv: any) => vv.id === vesselVoyageId);
    const routingIdOption = routingIdOptions.find(opt => opt.id === routingIdId);

    const destName = destination?.name ?? '- - -';
    const vesselText = vesselVoyage ? `${vesselVoyage.vesselName} (${vesselVoyage.voyageNo})` : '- - -';
    const routing = routingIdOption?.name ?? (routingIdId || '- - -');
    const transitText = transitTime ? `${transitTime} days` : '- - -';
    const stuffingText = stuffingDate ? this.datePipe.transform(stuffingDate, 'dd-MM-yyyy') : '- - -';
    const etdPolText = etdPol ? this.datePipe.transform(etdPol, 'dd-MM-yyyy') : '- - -';

    const text =
      `Destination - ${destName}\nVessel - ${vesselText}\n- Routing - ${routing}\n- Transit Time: ${transitText}\n- TNTV Stuffing: ${stuffingText}\n- ETD POL: ${etdPolText}`;

    navigator.clipboard.writeText(text).then(() => {
      this.utilityService.toastrInfoMessage('Row copied to clipboard');
    });
  }

  copySelectedRowsToClipboard(): void {
    const selectedRows = this.planningDetails.controls.filter(ctrl => ctrl.get('select')?.value === true);

    if (!selectedRows.length) {
      this.utilityService.toastrInfoMessage('Please select at least one row to copy');
      return;
    }

    const blocks = selectedRows.map(element => {
      const destinationId = element.get('destinationId')?.value;
      const vesselVoyageId = element.get('vesselVoyageId')?.value;
      const routingIdId = element.get('routingId')?.value;
      const routingIdOptions: any[] = element.get('routingIdOptions')?.value || [];
      const transitTime = element.get('transitTime')?.value;
      const stuffingDate = element.get('tentativeStuffingDate')?.value;
      const etdPol = element.get('etdPol')?.value;

      const destination = this.destinationDD?.find((d: any) => d.id === destinationId);
      const vesselVoyage = this.vesselVoyages?.find((vv: any) => vv.id === vesselVoyageId);
      const routingIdOption = routingIdOptions.find(opt => opt.id === routingIdId);

      const destName = destination?.name ?? '- - -';
      const vesselText = vesselVoyage ? `${vesselVoyage.vesselName} (${vesselVoyage.voyageNo})` : '- - -';
      const routing = routingIdOption?.name ?? (routingIdId || '- - -');
      const transitText = transitTime ? `${transitTime} days` : '- - -';
      const stuffingText = stuffingDate ? this.datePipe.transform(stuffingDate, 'dd-MM-yyyy') : '- - -';
      const etdPolText = etdPol ? this.datePipe.transform(etdPol, 'dd-MM-yyyy') : '- - -';

      return `Destination - ${destName}\nVessel ${vesselText}\n- Routing - ${routing}\n- Transit Time: ${transitText}\n- TNTV Stuffing: ${stuffingText}\n- ETD POL: ${etdPolText}`;
    });

    navigator.clipboard.writeText(blocks.join('\n\n')).then(() => {
      this.utilityService.toastrInfoMessage(`${selectedRows.length} row(s) copied to clipboard`);
    });
  }

  downloadPlanningImage(): void {
    if (!this.isEmailDialogEnabled()) {
      this.utilityService.toastrInfoMessage("Please select Week & POL to proceed");
      return;
    }
    // Get all visible rows
    const allRows = this.weeklyDetailsDataSource.getValue();
    // Get selected rows
    const selectedRows = allRows.filter(
      row => row.get('select')?.value
    );
    // Use selected rows if available, otherwise use all rows
    const rowsToExport =
      selectedRows.length > 0 ? selectedRows : allRows;
    // Prepare image data
    this.planningImageData = rowsToExport
      .map(row => {
        const destination = this.destinationDD?.find(
          d => d.id === row.get('destinationId')?.value
        );
        const vesselVoyage = this.vesselVoyages?.find(
          v => v.id === row.get('vesselVoyageId')?.value
        );
        return {

          destination: destination?.name || null,
          vesselName: vesselVoyage ? `${vesselVoyage.vesselName} / ${vesselVoyage.voyageNo}` : null,
          stuffingDate: row.get('tentativeStuffingDate')?.value || null,
          sailingDate: row.get('etdPol')?.value || null
        };
      })

      // Sort alphabetically by destination
      .sort((a, b) =>
        (a.destination || '').localeCompare(b.destination || '')
      );

    this.isGeneratingImage = true;
    // Wait for template rendering
    setTimeout(() => {
      html2canvas(this.planningTemplate.nativeElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false
      })
        .then(canvas => {
          canvas.toBlob(blob => {
            if (!blob) { return; }
            const fileName =
              `consol-planning-${this.datePipe.transform(new Date(), 'dd-MM-yyyy')}.png`;
            saveAs(blob, fileName);
          }, 'image/png');
        })
        .finally(() => {
          this.isGeneratingImage = false;
        });
    }, 200);
  }

  toggleColumnFilter(): void {
    this.columnFilterOpen = !this.columnFilterOpen;
  }

  toggleColumnVisibility(key: string): void {
    if (this.visibleColumnKeys.has(key)) {
      this.visibleColumnKeys.delete(key);
    } else {
      this.visibleColumnKeys.add(key);
    }
    this.weeklyColumns = this.visibleColumnKeys.size === 0
      ? [...this.allColumnOrder]
      : this.allColumnOrder.filter(col => col === 'select' || col === 'copyRow' || this.visibleColumnKeys.has(col));
  }

  clearColumnFilter(): void {
    this.visibleColumnKeys.clear();
    this.weeklyColumns = [...this.allColumnOrder];
  }

  public getAllPermission() {
    let permissionCodes = [PermissionKey.SHARE_WEEKLY_CONSOL_PLANNING_VIEW, PermissionKey.SHARE_WEEKLY_CONSOL_PLANNING_EDIT, PermissionKey.SHARE_WEEKLY_CONSOL_PLANNING_SEND];
    let permissionsWithShowValue: IPermissionWithShow[] = this.permissionService.checkPermissions(permissionCodes, PermissionKey.SHARE_WEEKLY_CONSOL_PLANNING);

    this.editPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.SHARE_WEEKLY_CONSOL_PLANNING_EDIT })?.show ? true : false;
    this.viewPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.SHARE_WEEKLY_CONSOL_PLANNING_VIEW })?.show ? true : false;
    this.sendPermission = _.findWhere(permissionsWithShowValue, { permissionCode: PermissionKey.SHARE_WEEKLY_CONSOL_PLANNING_SEND })?.show ? true : false;
  }

  get polId() { return this.shareWeeklyConsolForm.get('polId'); }
  get planningDetails(): FormArray { return this.shareWeeklyConsolForm.get('planningDetails') as FormArray; }
  get id() { return this.shareWeeklyConsolForm.get('id'); }

}
